import { makeObservable, observable, computed, action, runInAction } from 'mobx';
import { CACHE_CONFIG, AGGR_CHAT_USER } from '../constants/constants';
import { cacheManager } from '../utils/cacheManager';
import {
  fetchTableListService,
  fetchViewDataService,
  fetchTableDetailService,
} from '../apis/erpApi';
import { messageStore } from './MessageStore';

/**
 * ERP 表数据查询状态管理
 */
class ErpDatabaseStore {
  constructor() {
    // 状态属性
    this._tables = [];
    this._isLoading = false;
    this._error = null;
    this._lastFetchTime = null;
    this._viewData = null;
    this._isViewLoading = false;
    this._viewError = null;
    this._chatUserTableDetail = null;

    // 添加请求去重机制
    this._pendingRequests = new Map();

    makeObservable(this, {
      // 可观察状态
      _tables: observable,
      _isLoading: observable,
      _error: observable,
      _lastFetchTime: observable,
      _viewData: observable,
      _isViewLoading: observable,
      _viewError: observable,
      _chatUserTableDetail: observable,
      _pendingRequests: observable,

      // 计算属性
      tables: computed,
      isLoading: computed,
      error: computed,
      hasError: computed,
      isStale: computed,
      viewData: computed,
      isViewLoading: computed,
      viewError: computed,
      chatUserTableDetail: computed,

      // 动作
      fetchTableList: action,
      setTables: action,
      setViewData: action,
      setError: action,
      reset: action,
      fetchViewData: action,
      fetchTableDetail: action,
      initializeChatUserTable: action,
      forceRefreshTableDetail: action,
      clearTableDetailCache: action,
    });
  }

  // 计算属性
  get tables() {
    return this._tables;
  }

  get isLoading() {
    return this._isLoading;
  }

  get error() {
    return this._error;
  }

  get hasError() {
    return this._error !== null;
  }

  get isStale() {
    if (!this._lastFetchTime) return true;
    return Date.now() - this._lastFetchTime > CACHE_CONFIG.DURATION;
  }

  get viewData() {
    return this._viewData;
  }

  get isViewLoading() {
    return this._isViewLoading;
  }

  get viewError() {
    return this._viewError;
  }

  get chatUserTableDetail() {
    return this._chatUserTableDetail;
  }

  // 动作
  setTables(tables) {
    this._tables = tables;
    this._lastFetchTime = Date.now();
    this._error = null;
  }

  setViewData(viewData) {
    this._viewData = viewData;
    this._viewError = null;
  }

  setError(error) {
    this._error = error;
    messageStore.error(error.message || '获取表列表失败');
  }

  reset() {
    this._tables = [];
    this._isLoading = false;
    this._error = null;
    this._lastFetchTime = null;
  }

  async fetchTableList() {
    // 如果已经有请求在进行中，直接返回该请求的 Promise
    if (this._pendingRequests.has('tableList')) {
      return this._pendingRequests.get('tableList');
    }

    const cacheKey = 'erp_tables_list';
    const cachedData = cacheManager.get(cacheKey, CACHE_CONFIG.DURATION);

    if (cachedData) {
      runInAction(() => {
        this._tables = cachedData;
      });
      return { success: true, data: cachedData };
    }

    // 创建请求 Promise 并存储到 pendingRequests 中
    const requestPromise = fetchTableListService();
    this._pendingRequests.set('tableList', requestPromise);

    const response = await requestPromise;

    if (response.success && Array.isArray(response.data)) {
      runInAction(() => {
        this._tables = response.data;
        this._error = null;
      });
      cacheManager.set(cacheKey, response.data);
      return response;
    } else {
      const error = new Error(response.message || '获取表列表失败：响应格式错误');
      runInAction(() => {
        this._error = error;
        this._tables = [];
      });
      // 不抛出错误，返回错误响应对象
      return response;
    }
  }

  async fetchViewData(request, config = {}) {
    const data = await fetchViewDataService(request, config);
    return data;
  }

  async fetchTableDetail(tableId) {
    // 如果已经有相同的请求在进行中，直接返回该请求的 Promise
    if (this._pendingRequests.has(tableId)) {
      return this._pendingRequests.get(tableId);
    }

    // 直接使用表详情缓存键获取缓存数据
    const cacheKey = cacheManager.getTableDetailCacheKey(tableId);
    const cachedData = localStorage.getItem(cacheKey);

    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      const now = Date.now();
      if (now - timestamp <= CACHE_CONFIG.TABLE_STRUCTURE_DURATION) {
        return data;
      } else {
        localStorage.removeItem(cacheKey);
      }
    }

    // 创建请求 Promise 并存储到 pendingRequests 中
    const requestPromise = fetchTableDetailService(tableId);
    this._pendingRequests.set(tableId, requestPromise);

    const data = await requestPromise;

    // 使用新的表详情缓存方法
    cacheManager.setTableDetail(tableId, data);

    return data;
  }

  /**
   * 初始化聊天用户表数据
   */
  async initializeChatUserTable() {
    // 如果已经初始化过，直接返回
    if (this._chatUserTableDetail) {
      return;
    }

    // 使用新的表详情缓存键
    const cachedData = cacheManager.get(
      cacheManager.getTableDetailCacheKey(AGGR_CHAT_USER),
      CACHE_CONFIG.TABLE_STRUCTURE_DURATION
    );

    if (cachedData) {
      runInAction(() => {
        this._chatUserTableDetail = cachedData;
      });
      return;
    }

    // 使用 fetchTableDetail 方法，它会自动处理缓存和去重
    const data = await this.fetchTableDetail(AGGR_CHAT_USER);

    runInAction(() => {
      this._chatUserTableDetail = data;
    });
  }

  /**
   * 强制刷新表详情（忽略缓存）
   * @param {string} tableId - 表ID
   * @returns {Promise<Object>} 表详情数据
   */
  async forceRefreshTableDetail(tableId) {
    // 清除表详情缓存
    cacheManager.removeTableDetail(tableId);

    // 重新获取数据
    const data = await fetchTableDetailService(tableId);

    // 更新缓存
    cacheManager.setTableDetail(tableId, data);

    return data;
  }

  /**
   * 清除特定表的缓存
   * @param {string} tableId - 表ID
   */
  clearTableDetailCache(tableId) {
    if (tableId) {
      cacheManager.removeTableDetail(tableId);
    }
  }
}

// 创建单例实例
const erpDatabaseStore = new ErpDatabaseStore();

export default erpDatabaseStore;
